// Node-safe half of the shader registry — no import.meta.glob, no app imports. Shared by the browser runtime (via ./index.js) and scripts/shader-thumbs.mjs, which imports shader modules and this file directly in node.

import { rectCore, rectRingTurn, writeTurn } from '../surfaceField/phase.js'
import { DEFAULT_VECTOR, PARAMS, PIPELINE } from './pipeline/index.js'
import main from './pipeline/main.glsl.js'
import palette from './pipeline/palette.glsl.js'
import { seaAt } from './pipeline/sea.js'
import { sortParams, VOCAB } from './vocab.js'

export { PIPELINE }

/** The GLSL uniform name a param binds to. */
export const uniformName = (key, def) => def.uniform || 'u' + key[0].toUpperCase() + key.slice(1)

/** The params bound to uniforms in the shader's build: a subset's free ids (`bound`), else its params. */
const boundOf = shader => shader.bound ?? shader.params

/** The uniform name of the param declaring `rate: true` — the clock's rate, integrated per frame so changing it bends time only forward (no phase jump). Null when the shader declares none. */
export const rateUniformOf = shader => {
	for (const [key, def] of Object.entries(boundOf(shader))) if (def.rate) return uniformName(key, def)
	return null
}

/** Clock seconds per second at the `rate: true` param's ×1: its `base`, else 1. */
export const rateBaseOf = shader => Object.values(boundOf(shader)).find(def => def.rate)?.base ?? 1

/** The params declaring `integrate: true` — a RATE the runtime accumulates into its own uniform per frame, so the GLSL reads the accumulated QUANTITY (an angle, a distance) and never `rate * uTime`. Multiplying a rate by absolute time is discontinuous the moment the rate changes: the picture jumps by `Δrate * uTime`, worse the longer it has run. Integrating is the only shape that stays smooth, exactly as the clock's `rate: true` does for time itself.
 *
 * `settle: <quantum>` opts a param into COASTING TO REST at rate 0. Without it the accumulator simply freezes wherever it stood, which for a quantity indexing a palette leaves the picture stranded mid-transition with no way back — rate 0 stops the motion but cannot undo the offset. With it the runtime eases the accumulator to the nearest multiple of the quantum, so zeroing the rate returns the pattern to a resting state. Omit for a quantity that already wraps (a distance modulo a period), where any value is a valid rest.
 *
 * `per: [uniforms]` integrates the rate IN THAT UNIT, their sum: each frame adds `rate * dt / unit`, read from the live uniforms. A distance the GLSL divides by a resizable period must accumulate already divided — otherwise resizing the period rescales the whole accumulated travel and the pattern jumps by `travel * Δ(1/period)`. `{ plane, rows, rowsMin }` takes `rows` (floored at `rowsMin`) while `uRows` > 0.
 *
 * `coast: true`: at rate 0 under rows, the travel coasts on the way it last went to where count's rows sit centred (whole rows for an odd count, half for an even one).
 *
 * `wall: true` integrates on real time, not the clock a `rate` param scales: a side rate riding a fast clock passes the frame rate and aliases.
 *
 * `live: true` also uploads the rate in force (× `base`, per second of its clock) to `<uniform>Rate`, for GLSL that must know how fast the quantity grows now: what a value moving with the clock held when it entered the picture.
 *
 * `along: '<key>'` integrates over that carrier's travel instead of time: each frame adds `rate × base` per su the carrier moved (`travelSu`), so a phase that grows down a path bends only what lies ahead when its rate changes. `times: { uniform, power }` scales that by the uniform's live value to the power (`alongScale`). `live` uploads the rate per su.
 *
 * `depth: <su>` (on a carrier) adds an `along` integrator of its own travel at rate 1, folded by `depth`, uploaded to `<uniform>Depth`, its folds carried to `<uniform>DepthTurns`. Every `along` entry follows the entries without one. */
export const integratedParamsOf = shader => {
	const all = Object.entries(boundOf(shader))
		.filter(([, def]) => def.integrate)
		.flatMap(([key, def]) => {
			const p = {
				key,
				uniform: uniformName(key, def),
				settle: def.settle,
				per: def.per,
				base: def.base ?? 1,
				cycle: def.cycle,
				carry: def.carry,
				wall: def.wall,
				live: def.live,
				coast: def.coast,
				along: def.along,
				times: def.times,
			}
			return def.depth
				? [
						p,
						{
							key,
							uniform: depthUniform(p.uniform),
							base: 1,
							rate: 1,
							cycle: def.depth,
							along: key,
							carry: true,
						},
					]
				: [p]
		})
	return [...all.filter(p => !p.along), ...all.filter(p => p.along)]
}

/** The uniform a `depth` carrier uploads its folded travel in su to. */
export const depthUniform = uniform => `${uniform}Depth`

/** An `along` integrator's factor from its `times` uniform (1 without one). */
export const alongScale = (p, uniforms) => (p.times ? Math.max(uniforms[p.times.uniform] ?? 0, 0) ** p.times.power : 1)

/** A carrier's travel in su over `seconds` at `rate`. */
export const travelSu = (carrier, rate, seconds) => rate * carrier.base * seconds

/** The params declaring `hold: { step, steps }` — a value the picture must not show where the camera already is. The shader reads three uniforms, never the param's own: `<u>To` the value in force, `<u>From` the one before it, `<u>At` the clock at the change. It keeps `From` for the `steps` steps (each `step` clock seconds) from the one holding `<u>At`; the runtime moves `To` only while the clock is still in that step, or once it is `steps` steps away (`tickShaders`). */
export const heldParamsOf = shader =>
	Object.entries(boundOf(shader))
		.filter(([, def]) => def.hold)
		.map(([key, def]) => ({ key, uniform: uniformName(key, def), step: def.hold.step, steps: def.hold.steps }))

/** The three uniforms a `hold` param uploads. */
export const heldUniforms = uniform => ({ to: `${uniform}To`, from: `${uniform}From`, at: `${uniform}At` })

/** The uniform a `carry: true` param uploads its whole turns to: the cycles its fold removed, an exact integer in float32 for weeks of travel, so a palette that rides the quantity keeps its place across the fold. */
export const turnsUniform = uniform => `${uniform}Turns`

/** The uniform a `live: true` param uploads its rate in force to. */
export const liveRateUniform = uniform => `${uniform}Rate`

/** The divisor `per` names: the sum of those live uniforms, floored so a zero size never divides by zero. */
export const perUnit = (per, uniforms) => {
	if (!per) return 1
	const rows = !Array.isArray(per) && uniforms.uRows > 0
	const names = Array.isArray(per) ? per : rows ? per.rows : per.plane
	const gap = rows && per.rowsGap ? per.rowsGap.reduce((p, n) => p * uniforms[n], 1) : 0
	return Math.max(
		names.reduce((s, n) => s + uniforms[n], gap),
		rows ? per.rowsMin : 1e-4
	)
}

/** The uniforms a `per` reads. */
export const perUniforms = per =>
	!per ? [] : Array.isArray(per) ? per : [...per.plane, ...per.rows, ...(per.rowsGap ?? [])]

/** Where a coasting travel comes to rest from `acc` going `dir`: count's rows centred. */
export const coastTarget = (acc, count, dir) => {
	const p = Math.round(count) % 2 ? 0 : 0.5
	return dir > 0 ? Math.ceil(acc - p - 1e-6) + p : Math.floor(acc - p + 1e-6) + p
}

/** An integrated quantity folded into `[0, cycle)`: the picture repeats exactly at `cycle`, so the fold is invisible and float32 never sees hours of travel. */
export const wrapCycle = (value, cycle) => (cycle ? value - Math.floor(value / cycle) * cycle : value)

/** The `uTime` upload for a clock at `seconds`: folded by the module's declared `cycle`, the clock span after which the whole fragment repeats exactly. */
export const clockAt = (shader, seconds) => wrapCycle(seconds, shader.cycle)

/** What an integrator holds after `seconds` at a constant rate: the runtime's tick in closed form. */
export const integratedAt = ({ base, per, cycle }, rate, seconds, uniforms) =>
	wrapCycle((rate * base * seconds) / perUnit(per, uniforms), cycle)

/** `integratedAt` written into `uniforms`, with a `carry` param's whole turns beside it; an `along` param over `travel`, its carrier's su (`travelSu`). */
export const integrateInto = (uniforms, p, rate, seconds, travel = 0) => {
	const perSecond = p.along
		? (p.rate ?? rate) * p.base * alongScale(p, uniforms)
		: (rate * p.base) / perUnit(p.per, uniforms)
	const still = p.coast && rate === 0 && uniforms.uRows > 0
	const raw = still ? coastTarget(0, uniforms.uCount ?? 1, 1) : perSecond * (p.along ? travel : seconds)
	uniforms[p.uniform] = wrapCycle(raw, p.cycle)
	if (p.carry) uniforms[turnsUniform(p.uniform)] = Math.round((raw - uniforms[p.uniform]) / p.cycle)
	if (p.live) uniforms[liveRateUniform(p.uniform)] = perSecond
}

/** A module's `trail: { by, bins, bin, entry }` with its `travel` params, or null. Edits to a `travel` param ride the train its carriers (`by`, integrate params whose travel shares one unit) move past the view: within `entry` of the view's centre each point keeps the value it entered with; beyond it the current value waits. `travel: { phase: P }` paints the phase accumulated over 1/value per unit, folded by P, so a length change rolls in as a chirp; `travel: { phase: P, by: '<uniform>', scale: s }` paints value / (s · uniforms[by]) per unit, a frequency in a unit that uniform sets. */
export const trailOf = shader => {
	if (!shader.trail) return null
	const travelers = Object.entries(boundOf(shader))
		.filter(([, def]) => def.travel)
		.map(([key, def]) => ({
			key,
			uniform: uniformName(key, def),
			phase: def.travel.phase,
			by: def.travel.by,
			scale: def.travel.scale ?? 1,
		}))
	return travelers.length ? { ...shader.trail, travelers } : null
}

/** A phase traveler's phase over `units` of the train at value `v`. */
const phaseOver = (t, units, v, uniforms) =>
	t.by ? (units * v) / Math.max(t.scale * uniforms[t.by], 1e-6) : units / Math.max(v, 1e-3)

/** The vec4 array a `travel` param uploads its trail to; `TRAIL_AT` is the carriers' travel folded by the ring. */
export const trailUniform = uniform => `${uniform}Trail`
export const TRAIL_AT = 'uTrailAt'

const ringSlot = (j, n) => ((j % n) + n) % n

/** The ring's bins within `entry` of the view's centre at travel `at`, with the one the last interpolates toward; the rest split evenly beyond either side. */
const trailWindow = (trail, at) => {
	const lo = Math.floor((-trail.entry - at) / trail.bin)
	const hi = Math.floor((trail.entry - at) / trail.bin) + 1
	return { lo, hi, below: Math.floor((trail.bins - (hi - lo + 1)) / 2) }
}

/** Repaints every bin beyond the window with the current values, the phase continuing from the window's edge. */
const paintBeyond = (trail, state, uniforms) => {
	const { lo, hi, below } = trailWindow(trail, state.at)
	const n = trail.bins
	for (const t of trail.travelers) {
		const arr = state.slots[t.uniform]
		const v = uniforms[t.uniform]
		const step = t.phase ? phaseOver(t, trail.bin, v, uniforms) : 0
		const fill = (j, edge, m) =>
			(arr[ringSlot(j, n)] = t.phase ? wrapCycle(arr[ringSlot(edge, n)] + m * step, t.phase) : v)
		for (let m = 1; m <= below; m++) fill(lo - m, lo, -m)
		for (let m = 1; m <= n - (hi - lo + 1) - below; m++) fill(hi + m, hi, m)
	}
}

const uploadTrail = (trail, state, uniforms) => {
	for (const t of trail.travelers) uniforms[trailUniform(t.uniform)] = state.slots[t.uniform]
	uniforms[TRAIL_AT] = wrapCycle(state.at, trail.bins * trail.bin)
}

/** A trail at travel `at` that has held the current values all along. `array` makes each slot store (Float32Array for the GPU). */
export const steadyTrail = (trail, uniforms, at = 0, array = n => new Float32Array(n)) => {
	const { lo, below } = trailWindow(trail, at)
	const state = { at, slots: {} }
	for (const t of trail.travelers) {
		const arr = array(trail.bins)
		const v = uniforms[t.uniform]
		for (let s = 0; s < trail.bins; s++) {
			const j = lo - below + ringSlot(s - (lo - below), trail.bins)
			arr[s] = t.phase ? wrapCycle(phaseOver(t, j * trail.bin, v, uniforms), t.phase) : v
		}
		state.slots[t.uniform] = arr
	}
	uploadTrail(trail, state, uniforms)
	return state
}

/** Moves the train `step` units on: points crossing into the window carry what they held beyond it, the current values. */
export const advanceTrail = (trail, state, uniforms, step) => {
	state.at += step
	paintBeyond(trail, state, uniforms)
	uploadTrail(trail, state, uniforms)
}

/** The carriers' travel after `seconds` at constant rates, `wallSeconds` for a `wall` one: `integrateInto` unfolded. A carrier the variant does not bind is locked still. */
export const trailTravel = (shader, trail, values, seconds, wallSeconds = seconds, uniforms = {}) =>
	trail.by.reduce((sum, key) => {
		const def = boundOf(shader)[key]
		if (!def) return sum
		const p = { base: def.base ?? 1, per: def.per }
		return sum + integratedAt(p, values[key] ?? def.initial, def.wall ? wallSeconds : seconds, uniforms)
	}, 0)

/** One stage unit (su) in stage units: the stage's smaller side ÷ 200, so a size reads the same at any resolution. */
export const stageUnit = (width, height) => Math.min(width, height) / 200

const RECT_FIELD_MAX = 512
const RECT_FIELD_PAD = 2

/** The unmapped node's own `width × height` rect as a field, in its local space: the box SDF, exact; null at zero size. Here so the lab draws the stage's build. */
export const rectField = (width, height, { maxSize = RECT_FIELD_MAX } = {}) => {
	if (!(width > 0 && height > 0)) return null
	const pad = RECT_FIELD_PAD
	const inner = Math.max(1, maxSize - 2 * pad)
	const texel = Math.max(1, Math.max(width, height) / inner)
	const fit = v => Math.min(inner, Math.max(1, Math.ceil(v / texel - 1e-9)))
	const w = fit(width) + 2 * pad
	const h = fit(height) + 2 * pad
	const origin = [width / 2 - (w * texel) / 2, height / 2 - (h * texel) / 2]
	const hw = width / 2
	const hh = height / 2
	const data = new Float32Array(w * h * 4)
	let inradius = 0
	for (let y = 0; y < h; y++) {
		const py = origin[1] + (y + 0.5) * texel
		const qy = Math.abs(py - hh) - hh
		for (let x = 0; x < w; x++) {
			const px = origin[0] + (x + 0.5) * texel
			const qx = Math.abs(px - hw) - hw
			const r = -(Math.hypot(Math.max(qx, 0), Math.max(qy, 0)) + Math.min(Math.max(qx, qy), 0))
			const c = y * w + x
			data[c * 4] = r
			writeTurn(data, c, rectRingTurn(px, py, width, height, r))
			data[c * 4 + 3] = rectCore(px, py, width, height)
			if (r > inradius) inradius = r
		}
	}
	return { data, width: w, height: h, origin, texel, inradius, perimeter: 2 * (width + height), extent: null }
}

/** Uniform values for a thumbnail/preview render: every param at `initial`. The alpha gate is Pixi's `uColor` (object opacity), not a param — a standalone renderer must set it to opaque white itself. */
export const previewUniforms = shader => {
	const out = {}
	for (const [key, def] of Object.entries(shader.params)) out[key] = def.initial
	return out
}

/** A `kind: 'color'` param stores a hex string and binds a vec3 uniform; with `alpha`, a vec4 ('#rrggbb' reads opaque, no colour '' clear). */
export const isColorParam = def => def.kind === 'color'

const colorComponents = (value, def) => {
	if (def.alpha && value === '') return [0, 0, 0, 0]
	const c = hexToRgb01(value)
	return def.alpha ? [...c.slice(0, 3), c[3] ?? 1] : c.slice(0, 3)
}

/** The clock time every thumbnail is drawn at, so a preset's thumb reads as a variation of its shader's. */
export const THUMB_TIME = 3

/** A thumbnail's uniforms by GLSL name: `values` over each param's `initial`, a colour as rgb, an integrated quantity as `time` seconds of travel. */
export const thumbUniforms = (shader, values = {}, time = THUMB_TIME) => {
	const out = {}
	const bound = boundOf(shader)
	if (shader.variant) Object.assign(out, lockUniforms(shader.variant))
	for (const [key, def] of Object.entries(bound))
		out[uniformName(key, def)] = uniformValue(values[key] ?? def.initial, def)
	// After the loop: `per` reads the other uniforms.
	const integrated = integratedParamsOf(shader)
	const rateOf = key => values[key] ?? bound[key].initial
	for (const p of integrated) {
		const carrier = p.along && integrated.find(q => q.key === p.along)
		integrateInto(out, p, rateOf(p.key), time, carrier ? travelSu(carrier, rateOf(p.along), time) : 0)
	}
	const trail = trailOf(shader)
	if (trail) steadyTrail(trail, out, trailTravel(shader, trail, values, time, time, out), n => new Array(n))
	if (bound.wave_speed)
		seaAt(out, { seconds: time, rate: rateOf('wave_speed'), travel: (out.uSpeed ?? 0) + (out.uSpeedTurns ?? 0) })
	for (const { key, uniform } of heldParamsOf(shader)) {
		const names = heldUniforms(uniform)
		out[names.to] = out[names.from] = values[key] ?? bound[key].initial
		out[names.at] = 0
	}
	return out
}

/** surface.glsl.js's pixel-invariant uniforms for a draw of frame `extent`, mirroring its GLSL; self-contained, as pages inject its source. */
export const frameUniforms = (u, extent) => {
	const v = name => u[name] ?? 0
	const mix = (a, b, t) => a * (1 - t) + b * t
	const PI = 3.14159265
	const out = { uFoldLead: 0 }

	const enclose = v('uEnclose')
	const fold = v('uFold')
	if (enclose >= 1e-4 && fold > 0 && 1 - fold > 0) {
		const [ex, ey] = extent
		const fit = v('uFit')
		const rounding = v('uRounding')
		const a = (v('uAngle') * Math.PI) / 180
		const ax = Math.cos(a)
		const ay = Math.sin(a)
		const loS = Math.min(ax * ex, 0) + Math.min(ay * ey, 0)
		const spanS = Math.max(Math.max(ax * ex, 0) + Math.max(ay * ey, 0) - loS, 1)
		const loL = Math.min(-ay * ex, 0) + Math.min(ax * ey, 0)
		const span = Math.max(Math.max(-ay * ex, 0) + Math.max(ax * ey, 0) - loL, 1)
		const side = Math.max(Math.min(ex, ey) * 0.5, 1)
		const k = Math.floor(a / (0.5 * PI) + 0.5)
		const th = mix(a, k * 0.5 * PI, enclose)
		const far = Math.min((side * (1 - enclose)) / enclose, 1e5)
		const home = k - 2 * Math.floor(k / 2) > 0.5 ? [0.5 * ey, 0.5 * ex] : [0.5 * ex, 0.5 * ey]
		const ext = [mix(0.5 * spanS, home[0], enclose), mix(0.5 * span, home[1], enclose)]
		const r0 = mix(0.5 * spanS, side, enclose)
		const superNorm = (x, y) => {
			const m = Math.max(Math.abs(x), Math.abs(y))
			const e = 0.5 * rounding
			return e < 1e-3 || m < 1e-6 ? m : m * ((Math.abs(x) / m) ** (1 / e) + (Math.abs(y) / m) ** (1 / e)) ** e
		}
		const bentRing = (qx, qy, dx, dy) => {
			const px = qx - dx * far
			const py = qy - dy * far
			const rx = Math.cos(th) * px + Math.sin(th) * py
			const ry = -Math.sin(th) * px + Math.cos(th) * py
			const simple = r0 + far - superNorm(rx, ry)
			const bx = ext[0] + far
			const by = ext[1] + far
			const boxDepth = Math.min(bx - Math.abs(rx), by - Math.abs(ry))
			const m0 = Math.min(bx, by)
			const boxRound =
				m0 - Math.hypot(Math.max(Math.abs(rx) - (bx - m0), 0), Math.max(Math.abs(ry) - (by - m0), 0))
			return mix(simple, mix(boxDepth, boxRound, rounding), fit)
		}
		for (let i = 0; i < 9; i++) {
			const qx = ex * ((i % 3) * 0.5 - 0.5)
			const qy = ey * (Math.floor(i / 3) * 0.5 - 0.5)
			out.uFoldLead = Math.max(out.uFoldLead, bentRing(qx, qy, ax, ay) - bentRing(qx, qy, -ax, -ay))
		}
	}
	return out
}

/** displacement.glsl.js's `fieldLead` at a draw of frame `extent` (stage units, `unit` stage units per su): the cells of travel the blob field's sweep holds ahead of speed's. A build switch moves speed's travel by the old state's lead less the new one's, so the bands hold their place. */
export const fieldLead = (u, extent, unit) => {
	const v = name => u[name] ?? 0
	if (v('uBlobs') <= 0 || v('uRows') > 0) return 0
	const t = Math.min(Math.max((v('uStretch') - 0.6) / 0.4, 0), 1)
	const side = Math.max(0.5 * Math.min(...extent), 1)
	return (t * t * (3 - 2 * t) * side) / (Math.max(v('uWidth') + v('uSpacing'), 1e-4) * unit * v('uZoom'))
}

/** Draws one thumbnail frame of `fragment` on `gl`: a `size`² quad, Pixi's y-down projection, opaque `uColor`. Throws the compile or link log. Self-contained (`frameUniforms` aside, injected beside it), so the thumbs script injects it into its page; `programs` (a Map) caches a program per fragment. */
export const drawShaderFrame = (gl, { vertex, fragment, uniforms, size, time, programs }) => {
	uniforms = { ...uniforms, ...frameUniforms(uniforms, [size, size]) }
	let prog = programs?.get(fragment)
	if (!prog) {
		const compile = (type, src) => {
			const sh = gl.createShader(type)
			gl.shaderSource(sh, src)
			gl.compileShader(sh)
			if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(sh))
			return sh
		}
		prog = gl.createProgram()
		gl.attachShader(prog, compile(gl.VERTEX_SHADER, vertex))
		gl.attachShader(prog, compile(gl.FRAGMENT_SHADER, fragment))
		gl.linkProgram(prog)
		if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) throw new Error(gl.getProgramInfoLog(prog))
		programs?.set(fragment, prog)
	}
	gl.useProgram(prog)

	const buf = gl.createBuffer()
	gl.bindBuffer(gl.ARRAY_BUFFER, buf)
	gl.bufferData(
		gl.ARRAY_BUFFER,
		new Float32Array([0, 0, size, 0, 0, size, size, 0, size, size, 0, size]),
		gl.STATIC_DRAW
	)
	const loc = gl.getAttribLocation(prog, 'aPosition')
	gl.enableVertexAttribArray(loc)
	gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0)

	const u = name => gl.getUniformLocation(prog, name)
	const IDENTITY = [1, 0, 0, 0, 1, 0, 0, 0, 1]
	gl.uniformMatrix3fv(u('uProjectionMatrix'), false, [2 / size, 0, 0, 0, -2 / size, 0, -1, 1, 1])
	gl.uniformMatrix3fv(u('uWorldTransformMatrix'), false, IDENTITY)
	gl.uniformMatrix3fv(u('uTransformMatrix'), false, IDENTITY)
	gl.uniform2f(u('uResolution'), size, size)
	// stageUnit(size, size), inlined: the thumbs script injects this function's source alone.
	if (u('uStageUnit')) gl.uniform1f(u('uStageUnit'), size / 200)
	if (u('uTime')) gl.uniform1f(u('uTime'), time)
	// The alpha gate: Pixi binds uColor (tint × world alpha, premultiplied); a GLSL uniform left unset is 0 — an all-black thumb.
	if (u('uColor')) gl.uniform4f(u('uColor'), 1, 1, 1, 1)
	for (const [name, value] of Object.entries(uniforms)) {
		if (!u(name)) continue
		// A vec2 (`pan`), a colour, or a trail's vec4 array.
		if (Array.isArray(value))
			value.length === 2
				? gl.uniform2fv(u(name), value)
				: value.length === 3
					? gl.uniform3fv(u(name), value)
					: gl.uniform4fv(u(name), value)
		else gl.uniform1f(u(name), value)
	}

	gl.viewport(0, 0, size, size)
	gl.clearColor(0, 0, 0, 1)
	gl.clear(gl.COLOR_BUFFER_BIT)
	gl.drawArrays(gl.TRIANGLES, 0, 6)
	gl.deleteBuffer(buf)
}

/** '#rrggbb' → [0..1, 0..1, 0..1], '#rrggbbaa' with its alpha fourth; a malformed value reads as black rather than NaN in the uniform. */
export const hexToRgb01 = hex => {
	const m = typeof hex === 'string' ? /^#([0-9a-f]{6})([0-9a-f]{2})?$/i.exec(hex) : null
	const n = m ? Number.parseInt(m[1], 16) : 0
	const rgb = [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255]
	return m?.[2] ? [...rgb, Number.parseInt(m[2], 16) / 255] : rgb
}

/** The inverse, for snapshotting a mid-fade colour back into the bag's one storable spelling. */
export const rgb01ToHex = rgb =>
	`#${rgb
		.map(c =>
			Math.round(Math.min(1, Math.max(0, c)) * 255)
				.toString(16)
				.padStart(2, '0')
		)
		.join('')}`

// The GLSL `hsl` lib's conversions, for a colour tween that must agree with `mixHsl` on hue.
const rgbToHsl = ([r, g, b]) => {
	const mx = Math.max(r, g, b)
	const mn = Math.min(r, g, b)
	const d = mx - mn
	const l = (mx + mn) / 2
	if (d === 0) return [0, 0, l]
	const h = mx === r ? ((((g - b) / d) % 6) + 6) % 6 : mx === g ? (b - r) / d + 2 : (r - g) / d + 4
	return [h / 6, d / (1 - Math.abs(2 * l - 1)), l]
}

const hslToRgb = ([h, s, l]) => {
	const c = (1 - Math.abs(2 * l - 1)) * s
	const k = (((h % 1) + 1) % 1) * 6
	const x = c * (1 - Math.abs((k % 2) - 1))
	const rgb =
		k < 1 ? [c, x, 0] : k < 2 ? [x, c, 0] : k < 3 ? [0, c, x] : k < 4 ? [0, x, c] : k < 5 ? [x, 0, c] : [c, 0, x]
	return rgb.map(v => v + l - c / 2)
}

const shortHue = d => d - Math.round(d)

// Hues as `mixHsl` reads the pair: a grey end borrows the other's.
const pairHues = (a, b) => {
	const ha = a[1] < 1e-4 ? b[0] : a[0]
	return [ha, b[1] < 1e-4 ? ha : b[0]]
}

const PALETTE_PATH_SAMPLES = 64

// ⚠️ `mixHsl` takes the shorter A→B arc, so a path whose hue gap crosses ½ goes in HSL with the gap passing 0 instead (null otherwise).
export const palettePath = (a0, a1, b0, b1) => {
	if (![a0, a1, b0, b1].every(h => typeof h === 'string' && /^#[0-9a-f]{6}$/i.test(h))) return null
	const rgb = [a0, a1, b0, b1].map(hexToRgb01)
	const lerp = (x, y, t) => x.map((v, i) => v + (y[i] - v) * t)
	const gapAt = t => {
		const [ha, hb] = pairHues(rgbToHsl(lerp(rgb[0], rgb[1], t)), rgbToHsl(lerp(rgb[2], rgb[3], t)))
		return shortHue(hb - ha)
	}
	let flips = false
	for (let i = 0, g = gapAt(0); i < PALETTE_PATH_SAMPLES && !flips; i++) {
		const next = gapAt((i + 1) / PALETTE_PATH_SAMPLES)
		flips = Math.abs(next - g) > 0.5
		g = next
	}
	if (!flips) return null
	const [A0, A1, B0, B1] = rgb.map(rgbToHsl)
	const [hA0, hB0] = pairHues(A0, B0)
	const [hA1, hB1] = pairHues(A1, B1)
	const gap = shortHue(hB1 - hA1) - shortHue(hB0 - hA0)
	const sA = shortHue(hA1 - hA0)
	const dA = [sA - 1, sA, sA + 1].reduce((m, d) =>
		Math.abs(d) + Math.abs(d + gap) < Math.abs(m) + Math.abs(m + gap) ? d : m
	)
	const at = (h, d, x, y, t) => rgb01ToHex(hslToRgb([h + d * t, x[1] + (y[1] - x[1]) * t, x[2] + (y[2] - x[2]) * t]))
	return t =>
		t <= 0
			? { color: rgb01ToHex(rgb[0]), color_b: rgb01ToHex(rgb[2]) }
			: t >= 1
				? { color: rgb01ToHex(rgb[1]), color_b: rgb01ToHex(rgb[3]) }
				: { color: at(hA0, dA, A0, A1, t), color_b: at(hB0, dA + gap, B0, B1, t) }
}

// Shared GLSL a fragment pulls in with `#include "<name>"` on its own line; an unknown name stays put and fails the compile.
const GLSL_LIB = {
	// HSL round-trip. Interpolating in RGB drags every pair through grey; hue/sat/lightness keeps a red→blue sweep saturated the whole way, so a two-colour range reads as a spectrum rather than a fade.
	hsl: `vec3 rgbToHsl(vec3 c) {
	float mx = max(c.r, max(c.g, c.b));
	float mn = min(c.r, min(c.g, c.b));
	float l = (mx + mn) * 0.5;
	float d = mx - mn;
	if (d < 1e-6) return vec3(0.0, 0.0, l);
	// Denominator flips at mid-lightness: the same chroma spans a shrinking range as either end is approached.
	float s = d / (1.0 - abs(2.0 * l - 1.0));
	float h;
	if (mx == c.r) h = mod((c.g - c.b) / d, 6.0);
	else if (mx == c.g) h = (c.b - c.r) / d + 2.0;
	else h = (c.r - c.g) / d + 4.0;
	return vec3(h / 6.0, s, l);
}

vec3 hslToRgb(vec3 hsl) {
	float c = (1.0 - abs(2.0 * hsl.z - 1.0)) * hsl.y;
	float h = fract(hsl.x) * 6.0;
	float x = c * (1.0 - abs(mod(h, 2.0) - 1.0));
	vec3 rgb;
	if (h < 1.0) rgb = vec3(c, x, 0.0);
	else if (h < 2.0) rgb = vec3(x, c, 0.0);
	else if (h < 3.0) rgb = vec3(0.0, c, x);
	else if (h < 4.0) rgb = vec3(0.0, x, c);
	else if (h < 5.0) rgb = vec3(x, 0.0, c);
	else rgb = vec3(c, 0.0, x);
	return rgb + (hsl.z - c * 0.5);
}

// Hue is a CIRCLE, so the shorter arc is the one the eye reads as a direct sweep; lerping the raw 0..1 coordinate drags a red→magenta sweep through the entire spectrum.
vec3 mixHsl(vec3 a, vec3 b, float t) {
	vec3 x = rgbToHsl(a);
	vec3 y = rgbToHsl(b);
	// A greyscale end has no hue of its own — borrow the other's, or the sweep spins toward an arbitrary 0.
	if (x.y < 1e-4) x.x = y.x;
	if (y.y < 1e-4) y.x = x.x;
	float dh = fract(y.x - x.x + 0.5) - 0.5;
	return hslToRgb(vec3(x.x + dh * t, mix(x.y, y.y, t), mix(x.z, y.z, t)));
}`,
	palette: `// Exact for whole numbers below 2^24: a quotient one ULP off lands one m out, and the second pass brings it back.
float wrapIndex(float x, float m) {
	x -= m * floor(x / m);
	return x - m * floor(x / m);
}

vec3 paletteEntry(vec3 a, vec3 b, float i, float n, float blur) {
	return mixHsl(a, b, mix(i / (n - 1.0), 0.5, blur));
}

// Ramp is a cycle of n entries, each crossfading into the next and the last into the first; Mirror walks A→B→A over 2(n-1) steps. \`blur\` collapses toward the midpoint where bands alias.
vec3 paletteAt(vec3 a, vec3 b, float big, float small, float n, float mirror, float blur) {
	if (mirror > 0.5) {
		float m = 2.0 * (n - 1.0);
		float x = wrapIndex(wrapIndex(big, m) + small, m);
		return mixHsl(a, b, mix(1.0 - abs(x / (n - 1.0) - 1.0), 0.5, blur));
	}
	float x = wrapIndex(wrapIndex(big, n) + small, n);
	float i = floor(x);
	return mix(paletteEntry(a, b, i, n, blur), paletteEntry(a, b, wrapIndex(i + 1.0, n), n, blur), x - i);
}

// Spread renders fractionally: between two whole counts the palettes crossfade, so a sweep never steps.
vec3 palette(vec3 a, vec3 b, float big, float small, float spread, float mirror, float blur) {
	float n = floor(max(spread, 2.0));
	float f = max(spread, 2.0) - n;
	vec3 lo = paletteAt(a, b, big, small, n, mirror, blur);
	return f > 0.0 ? mix(lo, paletteAt(a, b, big, small, n + 1.0, mirror, blur), f) : lo;
}`,
	// Smooth 2D value noise and a 4-octave fbm, both in 0..1 and periodic in y over m whole lattice cells, so a noise clock folded by m never seams.
	noise: `float hash12(vec2 p) {
	vec3 p3 = fract(vec3(p.xyx) * 0.1031);
	p3 += dot(p3, p3.yzx + 33.33);
	return fract((p3.x + p3.y) * p3.z);
}

float vnoise(vec2 p, float m) {
	vec2 i = floor(p);
	vec2 f = fract(p);
	vec2 u = f * f * (3.0 - 2.0 * f);
	float y0 = mod(i.y, m);
	float y1 = mod(i.y + 1.0, m);
	return mix(mix(hash12(vec2(i.x, y0)), hash12(vec2(i.x + 1.0, y0)), u.x), mix(hash12(vec2(i.x, y1)), hash12(vec2(i.x + 1.0, y1)), u.x), u.y);
}

// Octaves double y exactly, doubling the period with it.
float fbm(vec2 p, float m) {
	float v = 0.0;
	float a = 0.5;
	for (int i = 0; i < 4; i++) {
		v += a * vnoise(p, m);
		p = p * vec2(2.03, 2.0) + 17.1;
		m *= 2.0;
		a *= 0.5;
	}
	return v / 0.9375;
}`,
}

// The palette band family, one set of params on every shader that paints a colour range: hsl and palette inlined, so a fragment includes this alone.
GLSL_LIB.bands = `${GLSL_LIB.hsl}

${GLSL_LIB.palette}

uniform vec3 uColorA;
uniform vec3 uColorB;
uniform float uSpread;
uniform float uRepeat;
uniform float uWash;
uniform float uWashTurns;

// One palette colour per element; \`turns\`: the elements a carried travel folded away, so the colour keeps its place across the fold.
vec3 bandColor(float idx, float turns, float blur) {
	return palette(uColorA, uColorB, uWashTurns + turns, idx + uWash, uSpread, uRepeat, blur);
}`

/** The param fields a registry id supplies, so a module never repeats them; a module's own field wins (a `capped` range). */
export const VOCAB_FIELDS = ['label', 'unit', 'curve', 'step', 'minimum', 'maximum', 'initial']

/** A param with its registry fields filled in and its `type` from its `kind`. */
export const prepareParam = (key, def) => {
	const v = VOCAB[key]
	const own = v ? Object.fromEntries(VOCAB_FIELDS.filter(f => v[f] !== undefined).map(f => [f, v[f]])) : {}
	const merged = { ...own, ...v?.flags, ...def }
	return { type: merged.kind === 'color' ? 'string' : 'number', ...merged }
}

const resolveIncludes = src =>
	src.replace(/^[ \t]*#include[ \t]+"(\w+)"[ \t]*$/gm, (line, name) => GLSL_LIB[name] ?? line)

/** A pipeline subset (`expose`/`lock`, no `fragment`): its fragment is the pipeline built for its variant. */
export const isSubset = shader => Boolean(shader?.expose) && !shader.fragment

/** A pipeline id's param: the registry's fields under the pipeline's declaration (`PARAMS`). */
const pipelineParam = id => prepareParam(id, PARAMS[id] ?? {})

const fnv = text => {
	let h = 0x811c9dc5
	for (let i = 0; i < text.length; i++) h = Math.imul(h ^ text.charCodeAt(i), 0x01000193)
	return (h >>> 0).toString(36)
}

/** A pipeline build: `free` ids bound to uniforms, `locks` compiled in as constants. The key folds in the pipeline source, so a stage edit is a new program. */
export const pipelineVariant = (free, locks = {}) => {
	const ids = [...new Set(free)].sort()
	const fixed = Object.fromEntries(
		Object.keys(locks)
			.filter(id => !ids.includes(id))
			.sort()
			.map(id => [id, locks[id]])
	)
	return { key: `pipeline:${fnv(`${ids}|${JSON.stringify(fixed)}|${PIPELINE.source}`)}`, free: ids, locks: fixed }
}

/** The scaffold build's locks: colour-only ids at identity, `color` white, the ground opaque black (gaps). */
export const SCAFFOLD_LOCKS = {
	light: 0,
	fog: 0,
	rings: 0,
	wash: 0,
	color: '#ffffff',
	ground: '#000000',
}

/** `variant`'s scaffold build: only the shapes' borders, black on white (main.glsl.js `scaffold`). A standalone variant is returned as is. */
export const scaffoldVariant = variant => {
	if (!variant.free || variant.scaffold) return variant
	const built = pipelineVariant(
		variant.free.filter(id => !(id in SCAFFOLD_LOCKS)),
		{ ...variant.locks, ...SCAFFOLD_LOCKS }
	)
	return { ...built, key: `scaffold:${built.key}`, scaffold: true }
}

// Every stage reads `scaffoldUV`, so main can run the pipeline at a neighbour pixel.
const scaffoldSource = source => {
	for (const part of [main.head, main.glsl, palette.scaffold.at])
		if (!source.includes(part)) throw new Error(`scaffold build: source lacks \`${part.trim().split('\n')[0]}\``)
	const head = source.indexOf(main.head) + main.head.length
	const stages = source
		.slice(head)
		.replace(/\bvUV\b/g, 'scaffoldUV')
		.replace(main.glsl.replace(/\bvUV\b/g, 'scaffoldUV'), main.scaffold.glsl)
		.replace(palette.scaffold.at, palette.scaffold.glsl)
	return `${source.slice(0, head)}vec2 scaffoldUV;\n${stages}`.replace(/^(#version[^\n]*\n)/, '$1#define SCAFFOLD\n')
}

let pipelineBuild = null

/** The build a fill of `shader` runs: `{ key, fragment }` for a standalone module (key its name); for every subset, bag and path the one pipeline build, every id a uniform, so no fill ever waits on a link a fade, settle or Advanced edit asks for. A subset's locks are its uniforms' values (`boundParams`). */
export const variantOf = shader => {
	if (!shader.locks) return { key: shader.name, fragment: shader.fragment }
	pipelineBuild ??= pipelineVariant(PIPELINE.ids)
	return pipelineBuild
}

/** The ids a fill of `shader` sets from its bag: its exposed ids and every locked id `bag` holds (Advanced). */
export const heldIds = (shader, bag = {}) => [
	...Object.keys(shader.params),
	...Object.keys(shader.locks ?? {}).filter(id => bag[id] !== undefined),
]

/** The params a variant binds to uniforms: the subset's own for its exposed ids, the pipeline's (at the subset's lock as `initial`) for the rest. A standalone variant binds its params. */
export const boundParams = (shader, variant) => {
	if (!variant.free) return shader.params
	return Object.fromEntries(
		sortParams(
			variant.free.map(id => [
				id,
				shader.params[id] ??
					prepareParam(id, {
						...PARAMS[id],
						...(shader.locks && id in shader.locks ? { initial: shader.locks[id] } : {}),
					}),
			])
		)
	)
}

/** The states an id paints nothing in, `at` reading a vector by id; `reads` the ids the state is made of. `roll`: the plane (rows 0) at tilt 90 with no perspective lift (`camera()`'s hit offset is exactly 0). `tilt` and `elevation`: the tube (tunnel ≥ 1, rows 0), whose camera is the view axis. `twist` scales only the arm's depth down the tube (unwind below 1); `fit` shapes only rings and the tube; `rounding` those and the marches; `hill_rows`, `parallax` and `fill` shape only rows; a wave's length and speed only its waves; the blob ids shape only blobs short of stretch 1. */
export const INERT = {
	roll: {
		reads: ['rows', 'tunnel', 'tilt', 'perspective', 'elevation'],
		at: at =>
			at('rows') <= 0 && at('tunnel') <= 0 && at('tilt') === 90 && at('perspective') * at('elevation') === 0,
	},
	...Object.fromEntries(
		['tilt', 'elevation'].map(id => [
			id,
			{ reads: ['rows', 'tunnel'], at: at => at('rows') <= 0 && at('tunnel') >= 1 },
		])
	),
	twist: { reads: ['tunnel', 'unwind'], at: at => at('tunnel') <= 0 || at('unwind') >= 1 },
	fit: { reads: ['enclose', 'tunnel'], at: at => at('enclose') <= 0 && at('tunnel') <= 0 },
	rounding: { reads: ['enclose', 'tunnel'], at: at => at('enclose') <= 0 && at('tunnel') <= 0 },
	bend: { reads: ['tunnel'], at: at => at('tunnel') <= 0 },
	...Object.fromEntries(
		['bend_angle', 'wander', 'bend_roll'].map(id => [
			id,
			{ reads: ['bend', 'tunnel'], at: at => at('bend') <= 0 || at('tunnel') <= 0 },
		])
	),
	light_angle: { reads: ['light'], at: at => at('light') <= 0 },
	...Object.fromEntries(
		['hill_rows', 'parallax', 'fill'].map(id => [id, { reads: ['rows'], at: at => at('rows') <= 0 }])
	),
	wave_length: { reads: ['wave_height'], at: at => at('wave_height') === 0 },
	...Object.fromEntries(
		['b', 'c'].flatMap(w =>
			['length', 'speed'].map(p => [
				`wave_${w}_${p}`,
				{ reads: [`wave_${w}_height`, 'rows'], at: at => at(`wave_${w}_height`) === 0 || at('rows') <= 0 },
			])
		)
	),
	// Stretch 1 draws every blob out into the bands, so a path to a band subset keeps the blobs and moves stretch alone.
	blobs: { reads: ['stretch'], at: at => at('stretch') >= 1 },
	...Object.fromEntries(
		['blob_size', 'blob_soft', 'temperature'].map(id => [
			id,
			{ reads: ['blobs', 'stretch'], at: at => at('blobs') <= 0 || at('stretch') >= 1 },
		])
	),
}

export const isInert = (id, at) => Boolean(INERT[id]?.at(at))

// A value as numbers (a colour's rgb, a vec's axes, a number alone), `n` long when a number stands for every axis.
const components = (v, n = 1) => {
	const c = typeof v === 'string' ? hexToRgb01(v) : [v].flat().map(Number)
	return c.length === 1 && n > 1 ? Array(n).fill(c[0]) : c
}
const fromComponents = (c, like) => (typeof like === 'string' ? rgb01ToHex(c) : Array.isArray(like) ? c : c[0])
const bounds = (range, n) => range.map(b => (typeof b === 'number' ? Array(n).fill(b) : components(b, n)))

/** `shader`'s range for `id` as `[lo, hi]`, each a number (every axis) or a value; null admits any value (an exposed colour, an inert end). An exposed id's is its fader's, a locked id's its lock, or the registry's while `bag` holds it (Advanced); the module's `range` widens either for paths alone, unless `fader`. */
export const pathRange = (shader, id, { bag = {}, fader = false } = {}) => {
	const def = shader.params[id] ?? (bag[id] !== undefined ? pipelineParam(id) : null)
	if (def && isColorParam(def)) return null
	if (!fader && shader.range?.[id]) return shader.range[id]
	if (def) return [def.minimum ?? -Infinity, def.maximum ?? Infinity]
	const v = shader.locks[id] ?? DEFAULT_VECTOR[id]
	return [v, v]
}

/** `value` clamped into `range` (`pathRange`) per component. */
export const clampToRange = (value, range) => {
	if (!range) return value
	const c = components(value)
	const [lo, hi] = bounds(range, c.length)
	return fromComponents(
		c.map((v, i) => Math.min(Math.max(v, lo[i]), hi[i])),
		value
	)
}

/** Two ranges' common values, null when they share none. */
export const rangeOverlap = (a, b, n = 1) => {
	if (!a || !b) return a ?? b
	const [[la, ha], [lb, hb]] = [bounds(a, n), bounds(b, n)]
	const lo = la.map((v, i) => Math.max(v, lb[i]))
	const hi = ha.map((v, i) => Math.min(v, hb[i]))
	return lo.every((v, i) => v <= hi[i]) ? [lo, hi] : null
}

const COUNT_ALL = VOCAB.count.maximum
// The tube's radius in su, a radius down its axis too (surface.glsl.js TUBE_RADIUS).
const TUBE_RADIUS = 100

/** Ids whole at rest that a path carries fractional (`pathTween`): a change to one walks the path even on one build. */
export const steppedMoves = (a, fromBag, b, toBag) =>
	['count', 'spread'].some(id => (fromBag[id] ?? a.params[id]?.initial) !== (toBag[id] ?? b.params[id]?.initial))

/** The lines a `count` shows (lattice.glsl.js `countLines`): itself below the stop under all, then without bound as it reaches all. */
export const countLines = c => (c < COUNT_ALL - 1 ? c : (COUNT_ALL - 1) / Math.max(COUNT_ALL - c, 0))

/** `countLines`' inverse: the count showing `n` lines. */
export const linesCount = n => (n < COUNT_ALL - 1 ? n : COUNT_ALL - (COUNT_ALL - 1) / n)

/** The lines that cover a `frame` (`{ extent, unit }`, stage units) along the flow at vector `at`: down the tube, the windings (rings or the arm's) until they pass under 2 px apart; rows' cap; else across the plane camera's view of the frame (lines under half a pixel apart left out), or from the outline to the centre round it. */
export const countFit = (at, { extent: [W, H], unit }) => {
	const pitch = Math.max(at('width') + at('spacing'), 1e-4)
	if (at('tunnel') >= 1) {
		// k/r winds deep at radius r (px: a pixel's slope off the axis, surface.glsl.js uvT), so windings are r² / (k · px) px apart; twist scales the arm's alone (lattice.glsl.js tube()).
		const twist = at('twist') + (1 - at('twist')) * at('unwind')
		const k = (Math.abs(twist) * TUBE_RADIUS) / pitch
		const px = at('perspective') / (200 * unit * at('zoom'))
		return Math.max(Math.ceil(Math.sqrt(k / (2 * px))) + 1, 1)
	}
	// Rows never reach all: count's top stop is that many rows.
	if (at('rows') > 0) return COUNT_ALL
	const su = unit * at('zoom') * pitch
	const rings = Math.min(W, H) / 2 / su + 2
	const a = (at('angle') * Math.PI) / 180
	const axis = [Math.cos(a), Math.sin(a)]
	const lo = Math.min(axis[0] * W, 0) + Math.min(axis[1] * H, 0)
	const span = Math.max(axis[0] * W, 0) + Math.max(axis[1] * H, 0) - lo
	// camera.glsl.js: a frame point's hit on the plane, its place across the bands.
	const r = (at('roll') * Math.PI) / 180
	const t = ((at('tilt') - 90) * Math.PI) / 180
	const [cp, sp] = [Math.sin(t), Math.cos(t)]
	const k = at('perspective') / (200 * unit)
	const e = at('elevation') * unit
	const pan = [at('pan')].flat().map(v => v * unit)
	const across = (x, y) => {
		const c0 = [x - W / 2, y - H / 2]
		const c = [Math.cos(r) * c0[0] - Math.sin(r) * c0[1], Math.sin(r) * c0[0] + Math.cos(r) * c0[1]]
		const den = sp - k * c[1] * cp
		if (den <= 1e-6) return null
		const hit = [(sp + k * e) * c[0], cp * (e + c[1] * cp) + (sp + k * e) * c[1] * sp].map(v => v / den)
		return (x + hit[0] - c[0] + pan[0]) * axis[0] + (y + hit[1] - c[1] + (pan[1] ?? pan[0])) * axis[1] - lo
	}
	let reach = 0
	const N = 32
	for (let i = 0; i <= N; i++)
		for (let j = 0; j <= N; j++) {
			const [x, y] = [(W * i) / N, (H * j) / N]
			const s = across(x, y)
			const s1 = across(x, y + 1)
			const s2 = across(x + 1, y)
			if (s === null || s1 === null || s2 === null) continue
			if (Math.hypot(s1 - s, s2 - s) > 2 * su) continue
			reach = Math.max(reach, Math.abs(s - span / 2))
		}
	const plane = (2 * reach) / su + 3
	const enclose = at('enclose')
	const n = enclose <= 0 ? plane : enclose >= 1 ? rings : Math.max(plane, rings)
	return Math.ceil(n)
}

/** Two subsets' path: the union `variant`; per free id each end's value, the `bridge` (the vector nearest A inside both ends' ranges; outside B's range where they share none) and both ends' `ranges`; the `shader` the union binds; given the `frame`, each end's `fit` (`countFit`). An end where an id is inert takes the other end's value (inert at both, B holds A's) and admits any. Null unless both are subsets. */
export const pathOf = (a, b, fromBag = {}, toBag = {}, frame = null) => {
	if (!a?.locks || !b?.locks) return null
	const at = (s, bag) => id => bag[id] ?? s.params[id]?.initial ?? s.locks[id] ?? DEFAULT_VECTOR[id]
	const [rawA, rawB] = [at(a, fromBag), at(b, toBag)]
	const atA = id => (isInert(id, rawA) && !isInert(id, rawB) ? rawB(id) : rawA(id))
	const atB = id => (isInert(id, rawB) ? atA(id) : rawB(id))
	const variant = variantOf(b)
	const from = {}
	const to = {}
	const bridge = {}
	const ranges = {}
	for (const id of variant.free) {
		from[id] = atA(id)
		to[id] = atB(id)
		const n = Math.max(components(from[id]).length, components(to[id]).length)
		const ra = isInert(id, rawA) ? null : pathRange(a, id, { bag: fromBag })
		const rb = isInert(id, rawB) ? null : pathRange(b, id, { bag: toBag })
		ranges[id] = [ra, rb]
		bridge[id] =
			JSON.stringify(from[id]) === JSON.stringify(to[id])
				? from[id]
				: clampToRange(
						n > 1 && typeof from[id] !== 'string' ? components(from[id], n) : from[id],
						rangeOverlap(ra, rb, n) ?? rb
					)
		// Rows draw only bands, so a path meets them on the plane: rows 0 at the bridge, whichever end has them.
		if (id === 'rows') bridge[id] = Math.min(from[id], to[id])
	}
	const fit = frame && variant.free.includes('count') ? [countFit(atA, frame), countFit(atB, frame)] : null
	return { variant, from, bridge, to, ranges, fit, shader: { ...b, params: { ...a.params, ...b.params } } }
}

/** How far a path's two legs overlap: 0 back to back, 1 both over the whole fade. A pair's own is judged by eye (`PATH_PAIRS`, keyed by both names sorted, `a|b`); this is the rest's. */
export const PATH_OVERLAP = 1
export const PATH_PAIRS = {
	'grid|plasma': 0.3,
	'cube_tunnel|spiral': 0.5,
	'grid|spiral': 0.5,
	'plasma|spiral': 0.5,
	// Rows draw until they reach the plane, and draw no contours: the blobs gather only after.
	'plasma|ribbons': 0,
	'ribbons|spiral': 0.5,
	'spiral|stripes': 0.5,
	'spiral|voxel_tunnel': 0.5,
}
export const pathOverlap = (a, b) => PATH_PAIRS[[a, b].sort().join('|')] ?? PATH_OVERLAP

const smooth = s => s * s * (3 - 2 * s)
const lerp = (a, b, t) => a + (b - a) * t
const unit = x => Math.min(Math.max(x, 0), 1)

// A `wrap` id turns the short way round on each leg.
const shortArc = e => {
	if (!PARAMS[e.key]?.wrap || typeof e.from !== 'number' || typeof e.to !== 'number') return e
	const span = VOCAB[e.key].maximum - VOCAB[e.key].minimum
	const near = (v, ref) => v - span * Math.round((v - ref) / span)
	const bridge = near(e.bridge, e.from)
	return { ...e, bridge, to: near(e.to, bridge) }
}

// Width + spacing at l along one leg: band frequency and width's share each linear.
const pitchAt = (w0, s0, w1, s1, l) => {
	const [p0, p1] = [w0 + s0, w1 + s1]
	if (p0 <= 0 || p1 <= 0) return [lerp(w0, w1, l), lerp(s0, s1, l)]
	const f = lerp(1 / p0, 1 / p1, l)
	const share = lerp(w0 / p0, w1 / p1, l)
	return [share / f, (1 - share) / f]
}

const same = (x, y) => JSON.stringify(x) === JSON.stringify(y)

/** A path's values at p. `entries` carry `from`, `bridge`, `to` (a colour's as rgb); `still` the values of ids that do not move (the colour pair's other half). Leg 1 takes each id from A to the bridge, leg 2 on to B, each half the fade at `overlap` 0 (a leg with nothing to move takes none); one smoothstep over the whole fade, so ids keep moving through the bridge. Clocks (an `integrate` rate) are linear over the fade; width + spacing by band frequency per leg; the colour pair by `palettePath` per leg; a `wrap` id by its short arc; `count`, given each end's `fit` (`pathOf`), by the lines it shows. `legs(p)` is each leg's progress. */
export const pathTween = (raw, { overlap = PATH_OVERLAP, still = {}, fit = null } = {}) => {
	const entries = raw.map(e => shortArc(e.bridge === undefined ? { ...e, bridge: e.from } : e))
	const isClock = e => Boolean(PARAMS[e.key]?.integrate)
	const staged = entries.filter(e => !isClock(e))
	const one = staged.some(e => !same(e.from, e.bridge))
	const two = staged.some(e => !same(e.bridge, e.to))
	const span = one && two ? 0.5 + 0.5 * unit(overlap) : 1
	const legs = t => {
		const s = smooth(unit(t))
		if (!one) return [1, s]
		if (!two) return [s, 0]
		return [unit(s / span), unit((s - (1 - span)) / span)]
	}
	// Leg 1 to l1 plus leg 2's move to l2, per component.
	const along = (x, b, y, l1, l2) => x + (b - x) * l1 + (y - b) * l2
	const w = entries.find(e => e.key === 'width')
	const sp = entries.find(e => e.key === 'spacing')
	const colors = ['color', 'color_b'].map(key => {
		const e = entries.find(x => x.key === key)
		if (e) return e
		const v = still[key] === undefined ? undefined : hexToRgb01(still[key])
		return { key, from: v, bridge: v, to: v }
	})
	const hex = v => (v ? rgb01ToHex(v) : undefined)
	const legPalette = (x, y) => palettePath(hex(x[0]), hex(y[0]), hex(x[1]), hex(y[1]))
	const palettes =
		colors.some(e => entries.includes(e)) && colors.every(e => e.from)
			? [
					legPalette(
						colors.map(e => e.from),
						colors.map(e => e.bridge)
					),
					legPalette(
						colors.map(e => e.bridge),
						colors.map(e => e.to)
					),
				]
			: null
	// The palette reads both spreads (repeatPalette) and spread's own tween as the progress between them, so each band moves once.
	const spread = entries.find(e => e.key === 'spread')
	// Count tweens the lines it shows, each end's all (or a count past it) the lines that cover the frame, so they grow in evenly; the ends land exactly.
	const count = fit && entries.find(e => e.key === 'count')
	const shown = count && [
		Math.min(countLines(count.from), fit[0]),
		Math.min(countLines(count.bridge), same(count.bridge, count.from) ? fit[0] : fit[1]),
		Math.min(countLines(count.to), fit[1]),
	]
	// Rows draw a path until its plane end, the plane's cross family over them (light()): it fades over the third of the fade at its crossed end.
	const rows = entries.find(e => e.key === 'rows' && e.from !== e.to)
	const cross = rows && entries.find(e => e.key === 'cross')
	const tween = t => {
		const [l1, l2] = legs(t)
		const values = {}
		for (const e of entries) {
			if (isClock(e)) {
				values[e.key] = lerp(e.from, e.to, unit(t))
				continue
			}
			if (e.step && !PARAMS[e.key]?.seam) {
				values[e.key] = l2 >= 0.5 ? e.to : l1 >= 0.5 ? e.bridge : e.from
				continue
			}
			const c = [e.from, e.bridge, e.to].map(v => components(v, components(e.to).length))
			const v = c[0].map((x, i) => along(x, c[1][i], c[2][i], l1, l2))
			values[e.key] = e.color ? rgb01ToHex(v) : e.vec ? v : v[0]
		}
		if (w && sp) {
			const [w1, s1] = pitchAt(w.from, sp.from, w.bridge, sp.bridge, l1)
			const [w2, s2] = pitchAt(w.bridge, sp.bridge, w.to, sp.to, l2)
			values.width = w1 + w2 - w.bridge
			values.spacing = s1 + s2 - sp.bridge
		}
		if (palettes?.some(Boolean)) {
			// Each leg's hue route, leg 2's added as its move from the bridge.
			const [p1, p2] = palettes
			const route = (p, x, y, l) =>
				colors.map((e, i) => {
					const at = p?.(l)
					return at ? hexToRgb01(at[e.key]) : x[i].map((v, k) => lerp(v, y[i][k], l))
				})
			const ends = k => colors.map(e => e[k])
			const r1 = route(p1, ends('from'), ends('bridge'), l1)
			const r2 = route(p2, ends('bridge'), ends('to'), l2)
			for (const [i, e] of colors.entries())
				values[e.key] = rgb01ToHex(r1[i].map((v, k) => v + r2[i][k] - e.bridge[k]))
		}
		if (spread) {
			values.spread_from = spread.from
			values.spread_to = spread.to
		}
		if (count) values.count = t <= 0 ? count.from : t >= 1 ? count.to : linesCount(along(...shown, l1, l2))
		if (cross) values.cross = lerp(cross.from, cross.to, smooth(unit(cross.to > cross.from ? 3 * t - 2 : 3 * t)))
		return values
	}
	tween.legs = legs
	return tween
}

const glslFloat = v => {
	const s = String(v)
	return /[.e]/.test(s) ? s : `${s}.0`
}
// The GLSL type a pipeline id binds: a colour vec3 (vec4 with alpha), else its declared `glsl` (`pan` vec2), else float.
const glslType = def => (isColorParam(def) ? (def.alpha ? 'vec4' : 'vec3') : (def.glsl ?? 'float'))
const glslValue = (value, def) => {
	if (isColorParam(def)) return `${glslType(def)}(${colorComponents(value, def).map(glslFloat).join(', ')})`
	const type = glslType(def)
	if (type === 'float') return glslFloat(Number(value))
	return `${type}(${[value]
		.flat()
		.map(v => glslFloat(Number(v)))
		.join(', ')})`
}

/** A param's value as its uniform takes it: a colour as rgb (rgba with alpha), a `vec2` id as two floats (a scalar on both axes). */
export const uniformValue = (value, def) => {
	if (isColorParam(def)) return colorComponents(value, def)
	if (def.glsl !== 'vec2') return value
	const xy = [value].flat()
	return [xy[0], xy[1] ?? xy[0]]
}

/** A variant's locked ids by uniform name at their lock: the GLSL holds them as constants, but `per` reads them beside the free uniforms. */
export const lockUniforms = variant =>
	Object.fromEntries(
		Object.entries(variant.locks ?? {}).map(([id, value]) => {
			const def = pipelineParam(id)
			return [uniformName(id, def), uniformValue(value, def)]
		})
	)

const variantSources = new Map()

/** A variant's fragment before the surface wrap: a standalone module's own; for a subset `#define LOCK_<ID>` per locked id, each id's uniforms (a constant under its macro), then `PIPELINE.source` with its `#include`s resolved. */
export const variantSource = variant => {
	if (variant.fragment !== undefined) return variant.fragment
	const cached = variantSources.get(variant.key)
	if (cached) return cached
	const lines = ['#version 300 es', 'precision highp float;']
	const macro = id => `LOCK_${id.toUpperCase()}`
	for (const [id, value] of Object.entries(variant.locks))
		lines.push(`#define ${macro(id)} ${glslValue(value, pipelineParam(id))}`)
	let trail = false
	for (const id of new Set([...PIPELINE.ids, ...variant.free, ...Object.keys(variant.locks)])) {
		const def = pipelineParam(id)
		// The clock's rate scales `uTime`; the GLSL never reads it.
		if (def.rate) continue
		const u = uniformName(id, def)
		const m = macro(id)
		const type = glslType(def)
		const derived = []
		if (def.carry) derived.push([turnsUniform(u), '0.0'])
		if (def.live) derived.push([liveRateUniform(u), '0.0'])
		if (def.depth) derived.push([depthUniform(u), '0.0'], [turnsUniform(depthUniform(u)), '0.0'])
		if (def.hold) {
			const h = heldUniforms(u)
			derived.push([h.to, m], [h.from, m], [h.at, '0.0'])
		}
		lines.push(
			`#ifdef ${m}`,
			`const ${type} ${u} = ${m};`,
			...derived.map(([n, c]) => `const float ${n} = ${c};`),
			'#else',
			`uniform ${type} ${u};`,
			...derived.map(([n]) => `uniform float ${n};`),
			'#endif'
		)
		if (def.travel) {
			trail = true
			lines.push(`uniform vec4 ${trailUniform(u)}[${PIPELINE.trail.bins / 4}];`)
		}
	}
	if (trail) lines.push(`uniform float ${TRAIL_AT};`)
	let source = `${lines.join('\n')}\n${resolveIncludes(PIPELINE.source.replace(/^\s*#version[^\n]*\n/, ''))}`
	if (variant.scaffold) source = scaffoldSource(source)
	variantSources.set(variant.key, source)
	return source
}

/** The fragment a mesh links for `variant` (`variantOf`) and field `filter`: the variant's source under the surface wrap. A standalone variant builds exactly its module's surfaced fragment. */
export const buildFragment = (variant, filter) =>
	surfaceFragment(variantSource(variant), { manualBilinear: filter === 'manual' })

// A subset: `params` its exposed ids (the registry's fields, then the pipeline's flags, then the module's), `locks` the default vector under its own `lock` less what it exposes, `variant` its build, `bound` that build's uniforms, `fragment` its source.
const prepareSubset = shader => {
	const params = Object.fromEntries(
		sortParams(Object.entries(shader.expose)).map(([id, def]) => [id, prepareParam(id, { ...PARAMS[id], ...def })])
	)
	const locks = { ...DEFAULT_VECTOR, ...shader.lock }
	for (const id of Object.keys(params)) delete locks[id]
	const prepared = { ...shader, params, locks, cycle: shader.cycle ?? PIPELINE.cycle, trail: PIPELINE.trail }
	prepared.variant = variantOf(prepared)
	prepared.bound = boundParams(prepared, prepared.variant)
	prepared.fragment = variantSource(prepared.variant)
	return prepared
}

/** The module with its params merged from the registry in its order and its `#include`s resolved — every loader (registry, thumbs, compile, lab) runs a module through this before use. A subset comes back as `prepareSubset` describes. */
export const prepareShader = shader =>
	isSubset(shader)
		? prepareSubset(shader)
		: {
				...shader,
				params: Object.fromEntries(
					sortParams(Object.entries(shader.params)).map(([key, def]) => [key, prepareParam(key, def)])
				),
				fragment: resolveIncludes(shader.fragment),
			}

// Shared vertex shader: a quad in the object's LOCAL space, vUV normalised against the object's own size — what lets an instance scale, position and group like any other object.
export const VERTEX = `#version 300 es
in vec2 aPosition;
out vec2 vUV;
uniform mat3 uProjectionMatrix;
uniform mat3 uWorldTransformMatrix;
uniform mat3 uTransformMatrix;
uniform vec2 uResolution;

void main() {
	mat3 mvp = uProjectionMatrix * uWorldTransformMatrix * uTransformMatrix;
	gl_Position = vec4((mvp * vec3(aPosition, 1.0)).xy, 0.0, 1.0);
	vUV = aPosition / uResolution;
}`

const UV_LINE = 'in vec2 vUV;'
const RESOLUTION_LINE = 'uniform vec2 uResolution;'
const OUT_LINE = 'out vec4 fragColor;'
// `mainImage(` and the like are not it: the name must close at the parenthesis.
const MAIN = /\bvoid\s+main\s*\(\s*(?:void\s*)?\)/g

/** Set when the renderer cannot filter 32F textures linearly: the field is then bound nearest and filtered by four taps in the ABI. */
export const MANUAL_BILINEAR_DEFINE = 'SURFACE_FIELD_MANUAL_BILINEAR'

// The ABI a body sees. A transition can blend two warmed endpoint fields at the same stage-space point.
const surfaceHeader = manualBilinear =>
	[
		...(manualBilinear ? [`#define ${MANUAL_BILINEAR_DEFINE}`] : []),
		// The fragment's position in the field's own space: stage for a surface, shape-local for an object.
		'in highp vec2 vField;',
		'in highp vec3 vFrame;',
		'in highp vec2 vStage;',
		// ⚠️ highp on every field read: Pixi prepends `precision mediump float` to a fragment that does not open with one, and a sampler defaults to lowp.
		'uniform highp sampler2D uField;',
		'uniform highp sampler2D uFieldTo;',
		// Field-space origin.xy and size.zw of the field's texture, so uv = (field − origin) / size.
		'uniform highp vec4 uFieldRect;',
		'uniform highp vec4 uFieldRectTo;',
		// Stage units per field unit (the placement's shorter axis; 1 for a surface): distances read in stage units.
		'uniform highp float uFieldScale;',
		'uniform highp float uFieldScaleTo;',
		'uniform highp vec2 uExtent;',
		'uniform highp vec2 uExtentTo;',
		'uniform highp float uInradius;',
		'uniform highp float uInradiusTo;',
		'uniform highp float uPerimeter;',
		'uniform highp float uPerimeterTo;',
		'uniform highp float uIndex;',
		'uniform highp float uSurfaceCount;',
		// 1 on a flow draw: vUV is the field's geodesic (u, v) and uResolution the river's length × mean width; 0 reads the linear frame.
		'uniform highp float uFlow;',
		'uniform highp float uFieldMorph;',
		// A flow draw's picture fit: (u, v) · xy + zw, a picture's aspect cropped to cover the river's (as `coverFrame` does on a linear frame); (1, 1, 0, 0) for a shader.
		'uniform highp vec4 uFlowCover;',
		'uniform highp vec4 uFlowCoverTo;',
		'uniform highp mat3 uToFieldTo;',
		'uniform highp mat3 uFrameTo;',
		'highp vec4 _field;',
		'highp vec2 _uv;',
		// Past the baked rect a tap clamps; distance must keep falling, or a morph's blended edge lags until the next key.
		'highp vec4 _beyondRect(highp vec4 s, highp vec2 pos, highp vec4 rect) {',
		'\ts.r -= length(pos - clamp(pos, rect.xy, rect.xy + rect.zw));',
		'\treturn s;',
		'}',
		'highp vec4 _fieldAt(highp vec2 pos) {',
		'\thighp vec2 uv = (pos - uFieldRect.xy) / uFieldRect.zw;',
		`#ifdef ${MANUAL_BILINEAR_DEFINE}`,
		'\tivec2 size = textureSize(uField, 0);',
		'\thighp vec2 t = uv * vec2(size) - 0.5;',
		'\thighp vec2 f = fract(t);',
		'\tivec2 i0 = ivec2(floor(t));',
		'\tivec2 hi = size - 1;',
		'\tivec2 a = clamp(i0, ivec2(0), hi);',
		'\tivec2 b = clamp(i0 + 1, ivec2(0), hi);',
		'\thighp vec4 s00 = texelFetch(uField, a, 0);',
		'\thighp vec4 s10 = texelFetch(uField, ivec2(b.x, a.y), 0);',
		'\thighp vec4 s01 = texelFetch(uField, ivec2(a.x, b.y), 0);',
		'\thighp vec4 s11 = texelFetch(uField, b, 0);',
		'\treturn _beyondRect(mix(mix(s00, s10, f.x), mix(s01, s11, f.x), f.y), pos, uFieldRect);',
		'#else',
		'\treturn _beyondRect(texture(uField, uv), pos, uFieldRect);',
		'#endif',
		'}',
		'highp vec4 _fieldAtTo(highp vec2 pos) {',
		'\thighp vec2 uv = (pos - uFieldRectTo.xy) / uFieldRectTo.zw;',
		`#ifdef ${MANUAL_BILINEAR_DEFINE}`,
		'\tivec2 size = textureSize(uFieldTo, 0);',
		'\thighp vec2 t = uv * vec2(size) - 0.5;',
		'\thighp vec2 f = fract(t);',
		'\tivec2 i0 = ivec2(floor(t));',
		'\tivec2 hi = size - 1;',
		'\tivec2 a = clamp(i0, ivec2(0), hi);',
		'\tivec2 b = clamp(i0 + 1, ivec2(0), hi);',
		'\thighp vec4 s00 = texelFetch(uFieldTo, a, 0);',
		'\thighp vec4 s10 = texelFetch(uFieldTo, ivec2(b.x, a.y), 0);',
		'\thighp vec4 s01 = texelFetch(uFieldTo, ivec2(a.x, b.y), 0);',
		'\thighp vec4 s11 = texelFetch(uFieldTo, b, 0);',
		'\treturn _beyondRect(mix(mix(s00, s10, f.x), mix(s01, s11, f.x), f.y), pos, uFieldRectTo);',
		'#else',
		'\treturn _beyondRect(texture(uFieldTo, uv), pos, uFieldRectTo);',
		'#endif',
		'}',
		'#define vUV _uv',
		// Distance to the outline in stage units, inside positive; the antialiased fringe outside reads as the edge.
		'#define SURFACE_EDGE_DIST max(_field.r, 0.0)',
		'#define SURFACE_INRADIUS mix(uInradius, uInradiusTo, uFieldMorph)',
		// (cos, sin) of the turn along the ring through the fragment, even along that ring's own length (phase.js); a flow draw holds (u, v) there instead.
		'#define SURFACE_RING_TURN _field.gb',
		'#define SURFACE_PERIMETER mix(uPerimeter, uPerimeterTo, uFieldMorph)',
		// Distance to the shape's core, its deepest cells, in stage units.
		'#define SURFACE_CORE_DIST _field.a',
		'#define SURFACE_INDEX uIndex',
		'#define SURFACE_COUNT uSurfaceCount',
		// True on a flow draw. Defined in every surfaced build, so `#ifdef SURFACE_FLOW` also tells a surfaced build from the plain one.
		'#define SURFACE_FLOW (uFlow > 0.5)',
	].join('\n')

// Samples the field, drops what lies clearly outside, runs the body, then fades the outline's last pixel by its signed distance. Distance and pixel size are both in stage units (× uFieldScale, exactly 1 for a surface).
const SURFACE_MAIN = `
void main() {
	// Both endpoint fields are sampled at the same stage position. Distances are converted to stage units before blending.
	highp vec4 fieldFrom = _fieldAt(vField);
	highp vec4 fieldTo = fieldFrom;
	highp vec3 frame = vFrame;
	if (uFieldMorph > 0.0) {
		fieldTo = _fieldAtTo((uToFieldTo * vec3(vStage, 1.0)).xy);
		// Blended in homogeneous coordinates like every other endpoint: taking the TO frame outright steps the picture once per morph key.
		frame = mix(vFrame, uFrameTo * vec3(vStage, 1.0), uFieldMorph);
	}
	_field = mix(fieldFrom, fieldTo, uFieldMorph);
	_field.r = mix(fieldFrom.r * uFieldScale, fieldTo.r * uFieldScaleTo, uFieldMorph);
	_field.a = mix(fieldFrom.a * uFieldScale, fieldTo.a * uFieldScaleTo, uFieldMorph);
	// Signed-distance derivatives follow stage-space pixel width through parent transforms.
	highp float px = max(0.5 * (length(dFdx(_field.r)) + length(dFdy(_field.r))), 1e-6);
	// A pixel of live fragments stays outside the coverage edge, so derivatives the body takes there still have neighbours.
	if (_field.r < -max(1.0, px)) discard;
	// Flow reads (u, v) from the field (g, b): unitless, so never × uFieldScale; dilated past the outline, so the fringe reads the edge's. Uniform per draw: no divergence.
	_uv = uFlow > 0.5 ? _field.gb : frame.xy / (abs(frame.z) < 1e-8 ? 1e-8 : frame.z);
	// A picture covers the river (uFlowCover, identity for a shader); a linear frame already carries its cover.
	if (uFlow > 0.5) _uv = _uv * mix(uFlowCover, uFlowCoverTo, uFieldMorph).xy + mix(uFlowCover, uFlowCoverTo, uFieldMorph).zw;
	_surfaceBody();
	fragColor *= smoothstep(-0.5 * px, 0.5 * px, _field.r);
}`

/** The build every shader mesh runs, mapped or not: `vUV` is the surface's 0–1 frame and `uResolution` its extent, so a fragment reads the node rect and a surface alike — on a flow draw (`SURFACE_FLOW`) `vUV` is the field's geodesic (u along, v across) and `uResolution` the river's length × mean width; `SURFACE_*` hooks a shader may `#ifdef` on. The shader's `main` becomes the body of a wrapper that samples the outline's field (`uField`), discards outside it and multiplies the result by a one-pixel coverage. Throws when a rewrite would miss: a fragment reading `uResolution` must declare it on the exact line, or it still links and renders against zero. `manualBilinear` filters the field by hand where 32F linear filtering is unavailable. */
export const surfaceFragment = (fragment, { manualBilinear = false } = {}) => {
	const required = fragment.includes('uResolution') ? [UV_LINE, OUT_LINE, RESOLUTION_LINE] : [UV_LINE, OUT_LINE]
	for (const line of required)
		if (!fragment.includes(line)) throw new Error(`surface build: fragment lacks the exact line \`${line}\``)
	const mains = fragment.match(MAIN)?.length ?? 0
	if (mains !== 1) throw new Error(`surface build: fragment must define \`void main()\` exactly once, found ${mains}`)
	return (
		fragment
			.replace(UV_LINE, surfaceHeader(manualBilinear))
			// After the declaration: the macro must not rename the uniform itself.
			.replace(RESOLUTION_LINE, `${RESOLUTION_LINE}\n#define uResolution mix(uExtent, uExtentTo, uFieldMorph)`)
			.replace(MAIN, 'void _surfaceBody()') + SURFACE_MAIN
	)
}

/** The surfaced vertex stage: a quad in world (stage) units for a mapped draw, the node's local rect otherwise. `uFrame` is world → 0–1 frame (divide by z), `uToField` world → the field's own space (identity for a surface and the rect, an object's inverse placement otherwise); both column-major. */
export const SURFACE_VERTEX = `#version 300 es
in vec2 aPosition;
out vec2 vField;
out vec3 vFrame;
out vec2 vStage;
uniform mat3 uProjectionMatrix;
uniform mat3 uWorldTransformMatrix;
uniform mat3 uTransformMatrix;
uniform mat3 uFrame;
uniform mat3 uToField;

void main() {
	mat3 mvp = uProjectionMatrix * uWorldTransformMatrix * uTransformMatrix;
	gl_Position = vec4((mvp * vec3(aPosition, 1.0)).xy, 0.0, 1.0);
	// Affine, so interpolating it is exact; the identity leaves a surface's position bit-for-bit.
	vField = (uToField * vec3(aPosition, 1.0)).xy;
	vStage = aPosition;
	// Linear in world position, so interpolating it and dividing per fragment is exact under any 2D placement.
	vFrame = uFrame * vec3(aPosition, 1.0);
}`
